import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonSiguiente, Input } from "@/components/ui";
import Alerta from "@/components/alert/Alerta";
import { AlertaType } from "@/types";
import { safeFetch } from "@/api/safeFetch";

export default function CrearAdmin() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const initialForm = {
    nombre_usuario: "",
    email: "",
    contrasena: "",
    nombre: "",
    apellidos: "",
    dni: "",
    nie: "",
  };

  const [form, setForm] = useState(initialForm);
  const [errores, setErrores] = useState<Partial<Record<keyof typeof initialForm, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [alerta, setAlerta] = useState<{ mensaje: string; type: AlertaType } | null>(null);

  // Quita errores al escribir
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name as keyof typeof form;
    setForm(prev => ({ ...prev, [key]: e.target.value }));
    setErrores(prev => ({ ...prev, [key]: false }));
  };

  // Clase dinámica para inputs
  const inputClass = (campo: keyof typeof form) =>
    `col-span-12 p-2 rounded border ${
      errores[campo] ? "bg-red-100 border-red-500" : "bg-white border-zinc-300"
    }`;

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
    
      const camposObligatorios: (keyof typeof form)[] = [
        "nombre_usuario",
        "email",
        "contrasena",
        "nombre",
        "apellidos",
      ];
    
      const nuevosErrores: Partial<Record<keyof typeof form, boolean>> = {};
    
      camposObligatorios.forEach(campo => {
        if (!form[campo]?.trim()) nuevosErrores[campo] = true;
      });
    
      if (!form.dni.trim() && !form.nie.trim()) {
        nuevosErrores.dni = true;
        nuevosErrores.nie = true;
      }
    
      setErrores(nuevosErrores);
      if (Object.keys(nuevosErrores).length > 0) return;
    
      const controller = new AbortController();
      const signal = controller.signal;
    
      setLoading(true);
    
      try {
        const res = await safeFetch("/api/superadmin/crear-admin", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...form, rol: "admin", verificado: 1 }),
          signal
        });
    
        const data = await res.json();
        if (!res.ok) throw new Error(data?.mensaje || "Error creando el admin");
    
        setAlerta({ mensaje: "Admin creado correctamente ✅", type: "success" });
        setForm(initialForm);
        setErrores({});
        setTimeout(() => navigate("/superAdmin"), 1200);
    
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setAlerta({ mensaje: err.message || "Error creando el admin", type: "error" });
        }
      } finally {
        setLoading(false);
      }
    
      return () => controller.abort();
    };
    

  useEffect(() => {
    return () => {
      setForm(initialForm);
      setErrores({});
      setLoading(false);
      setAlerta(null);
  
      // limpiar scroll congelado
      window.scrollTo(0, 0);
    };
  }, []);
  

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative">

      {/* Botón volver */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

      <div className="w-full max-w-2xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-cyan-500">
        <h2 className="text-2xl text-center font-bold mb-6">Crear nuevo Admin</h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-4">

          {/* Campos obligatorios */}
          {(["nombre_usuario", "email", "contrasena", "nombre", "apellidos"] as (keyof typeof form)[]).map(campo => (
            <div key={campo} className="relative col-span-12">
              <Input
                name={campo}
                type={campo === "email" ? "email" : campo === "contrasena" ? "password" : "text"}
                placeholder={campo.replace("_", " ").toUpperCase()}
                value={form[campo]}
                onChange={handleChange}
                className={inputClass(campo)}
              />
              {errores[campo] && <span className="absolute right-2 bottom-2 text-red-500 text-sm">*</span>}
            </div>
          ))}

          {/* DNI / NIE */}
          <div className="relative col-span-12">
            <Input
              name="dni"
              placeholder="DNI"
              value={form.dni}
              onChange={handleChange}
              className={inputClass("dni")}
            />
            {errores.dni && <span className="absolute right-2 bottom-2 text-red-500 text-sm">*</span>}
          </div>

          <div className="relative col-span-12">
            <Input
              name="nie"
              placeholder="NIE"
              value={form.nie}
              onChange={handleChange}
              className={inputClass("nie")}
            />
            {errores.nie && <span className="absolute right-2 bottom-2 text-red-500 text-sm">*</span>}
          </div>

          {/* Botón enviar */}
          <ButtonSiguiente
            type="submit"
            className="col-span-12 bg-cyan-600 text-white p-3 rounded hover:bg-cyan-700 transition"
            disabled={loading}
          >
            {loading ? "Creando..." : "Crear Admin"}
          </ButtonSiguiente>
        </form>
      </div>

      {/* Alerta */}
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}
    </div>
  );
};
